import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_MERCHANT_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/merchant"
).replace(/\/+$/, "");

const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

const pad2 = (value) => String(value).padStart(2, "0");
const dateToInput = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const getDefaultDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
  return { dateFrom: dateToInput(start), dateTo: dateToInput(end) };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T12:00:00+03:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value || "-");
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatTRY = (value) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDuration = (minutes, days) => {
  const d = Math.max(0, Number(days || 0));
  const m = Math.max(0, Number(minutes || 0));
  if (d > 0) return `${d} gün`;
  if (m <= 0) return "-";
  const hour = Math.floor(m / 60);
  const minute = m % 60;
  if (hour && minute) return `${hour} sa ${minute} dk`;
  if (hour) return `${hour} saat`;
  return `${minute} dk`;
};

const paymentLabel = (value) => {
  const key = String(value || "").toLowerCase();
  return ({
    card: "Kart",
    cash: "Nakit",
    multinet: "Multinet",
    sodexo: "Sodexo",
    setcard: "Setcard",
    metropol: "Metropol",
    metropolcard: "Metropol",
    edenred: "Edenred",
  })[key] || value || "-";
};

async function apiPost(path, body, { signal } = {}) {
  const token = getAuthToken();
  const card = getAuthCard();
  if (!token || !card) {
    redirectToLogin();
    throw new Error("Giriş bilgisi bulunamadı.");
  }

  const response = await fetch(`${API_BASE_URL}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ card, ...(body || {}) }),
    signal,
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (response.status === 401 || response.status === 403 || json?.redirectToLogin) {
    redirectToLogin();
    throw new Error("Oturum süresi doldu.");
  }
  if (!response.ok || json?.success === false) {
    throw new Error(json?.message || json?.error || `HTTP ${response.status}`);
  }
  return json;
}

function StatusBadge({ value }) {
  const status = String(value || "confirmed").toLowerCase();
  const classes = status === "cancelled"
    ? "border-rose-200 bg-rose-50 text-rose-700"
    : status === "confirmed"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-amber-200 bg-amber-50 text-amber-700";
  const label = status === "cancelled" ? "İptal" : status === "confirmed" ? "Onaylı" : status;
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${classes}`}>{label}</span>;
}

function TimingBadge({ row }) {
  if (row.mode === "day") {
    return <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-black text-indigo-700">Günlük üyelik</span>;
  }
  const appointmentAt = row.startAt ? new Date(row.startAt) : null;
  const isPast = appointmentAt && !Number.isNaN(appointmentAt.getTime()) && appointmentAt < new Date();
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ${isPast ? "bg-slate-100 text-slate-600" : "bg-sky-50 text-sky-700"}`}>
      {isPast ? "Geçmiş" : "Yaklaşan"}
    </span>
  );
}

function AppointmentDate({ row }) {
  if (row.mode === "day") {
    return (
      <div>
        <div className="font-black text-slate-900">{formatDate(row.startDate)}</div>
        <div className="mt-1 text-xs text-slate-500">{row.endDate && row.endDate !== row.startDate ? `${formatDate(row.endDate)} tarihine kadar` : "Tüm gün"}</div>
      </div>
    );
  }
  return (
    <div>
      <div className="font-black text-slate-900">{formatDate(row.startAt)}</div>
      <div className="mt-1 text-sm font-bold text-sky-700">{formatTime(row.startAt)} – {formatTime(row.endAt)}</div>
    </div>
  );
}

function AppointmentsPage() {
  const navigate = useNavigate();
  const defaults = useMemo(() => getDefaultDates(), []);
  const emptyFilters = useMemo(() => ({
    ...defaults,
    branchId: "all",
    personelId: "all",
    startTime: "",
    endTime: "",
    status: "all",
    customer: "",
    orderNumber: "",
    sortBy: "appointment",
    sortDirection: "asc",
  }), [defaults]);

  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [summary, setSummary] = useState({ total: 0, confirmed: 0, cancelled: 0, upcoming: 0, totalPrice: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const visiblePersonnel = useMemo(() => {
    if (draftFilters.branchId === "all") return personnel;
    return personnel.filter((item) => String(item.subeId) === String(draftFilters.branchId));
  }, [personnel, draftFilters.branchId]);

  const fetchRows = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const json = await apiPost("appointmentsreport", {
        ...appliedFilters,
        singlePage: true,
      }, { signal });
      setRows(Array.isArray(json.appointments) ? json.appointments : []);
      setBranches(Array.isArray(json.branches) ? json.branches : []);
      setPersonnel(Array.isArray(json.personnel) ? json.personnel : []);
      setSummary(json.summary || { total: 0, confirmed: 0, cancelled: 0, upcoming: 0, totalPrice: 0 });
    } catch (fetchError) {
      if (fetchError?.name !== "AbortError") setError(fetchError?.message || "Randevular alınamadı.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    const controller = new AbortController();
    fetchRows(controller.signal);
    return () => controller.abort();
  }, [fetchRows]);

  const applyFilters = (event) => {
    event?.preventDefault?.();
    if (draftFilters.dateFrom && draftFilters.dateTo && draftFilters.dateFrom > draftFilters.dateTo) {
      setError("Başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }
    if (draftFilters.startTime && draftFilters.endTime && draftFilters.startTime > draftFilters.endTime) {
      setError("Başlangıç saati bitiş saatinden büyük olamaz.");
      return;
    }
    setAppliedFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const onBranchChange = (value) => {
    setDraftFilters((current) => ({ ...current, branchId: value, personelId: "all" }));
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1900px] flex-col gap-4 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Randevular</h1>
            <p className="mt-1 text-sm text-slate-500">Randevu zamanı, alınma zamanı, şube, personel ve hizmet bilgilerini görüntüleyin.</p>
          </div>
          <nav className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/")} className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">Ürün Paneli</button>
            <button onClick={() => navigate("/designpanel")} className="rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-700">Dizayn</button>
            <button onClick={() => navigate("/ReceiptInfoPage")} className="rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-purple-700">Fiş Bilgi</button>
            <button onClick={() => navigate("/preferance")} className="rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-teal-700">Tercihler</button>
            <button onClick={() => navigate("/excelfis")} className="rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-600">Excel Fiş</button>
            <button onClick={() => navigate("/branches")} className="rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-900">Şubeler</button>
            <button type="button" disabled className="rounded-xl bg-fuchsia-600 px-4 py-2.5 text-sm font-black text-white opacity-90">Randevular</button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1900px] space-y-5 px-5 py-6">
        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 font-semibold text-rose-700">{error}</div> : null}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {[
            ["Eşleşen Randevu", summary.total || 0, "bg-white"],
            ["Onaylı", summary.confirmed || 0, "bg-emerald-50"],
            ["Yaklaşan", summary.upcoming || 0, "bg-sky-50"],
            ["İptal", summary.cancelled || 0, "bg-rose-50"],
            ["Toplam Tutar", formatTRY(summary.totalPrice || 0), "bg-amber-50"],
          ].map(([title, value, color]) => (
            <div key={title} className={`rounded-3xl border border-slate-200 p-5 shadow-sm ${color}`}>
              <div className="text-xs font-black uppercase tracking-wider text-slate-500">{title}</div>
              <div className="mt-2 text-2xl font-black text-slate-950">{value}</div>
            </div>
          ))}
        </section>

        <form onSubmit={applyFilters} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6">
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Başlangıç Tarihi</span><input type="date" value={draftFilters.dateFrom} onChange={(e) => setDraftFilters((c) => ({ ...c, dateFrom: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Bitiş Tarihi</span><input type="date" value={draftFilters.dateTo} onChange={(e) => setDraftFilters((c) => ({ ...c, dateTo: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Şube</span><select value={draftFilters.branchId} onChange={(e) => onBranchChange(e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3"><option value="all">Tüm Şubeler</option>{branches.map((item) => <option key={item.subeId} value={item.subeId}>{item.name}{item.city ? ` · ${item.city}` : ""}</option>)}</select></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Personel</span><select value={draftFilters.personelId} onChange={(e) => setDraftFilters((c) => ({ ...c, personelId: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3"><option value="all">Tüm Personeller</option>{visiblePersonnel.map((item) => <option key={item.personelId} value={item.personelId}>{item.name}{item.branchName ? ` · ${item.branchName}` : ""}</option>)}</select></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Saat Başlangıcı</span><input type="time" step="900" value={draftFilters.startTime} onChange={(e) => setDraftFilters((c) => ({ ...c, startTime: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Saat Bitişi</span><input type="time" step="900" value={draftFilters.endTime} onChange={(e) => setDraftFilters((c) => ({ ...c, endTime: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Durum</span><select value={draftFilters.status} onChange={(e) => setDraftFilters((c) => ({ ...c, status: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3"><option value="all">Tüm Durumlar</option><option value="confirmed">Onaylı</option><option value="cancelled">İptal</option></select></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Müşteri</span><input value={draftFilters.customer} onChange={(e) => setDraftFilters((c) => ({ ...c, customer: e.target.value }))} placeholder="İsim ara" className="h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Sipariş No</span><input value={draftFilters.orderNumber} onChange={(e) => setDraftFilters((c) => ({ ...c, orderNumber: e.target.value }))} placeholder="Sipariş no ara" className="h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Sıralama</span><select value={draftFilters.sortBy} onChange={(e) => setDraftFilters((c) => ({ ...c, sortBy: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3"><option value="appointment">Randevu zamanı</option><option value="booked">Alınma zamanı</option></select></label>
            <label className="space-y-1"><span className="text-xs font-black uppercase text-slate-500">Yön</span><select value={draftFilters.sortDirection} onChange={(e) => setDraftFilters((c) => ({ ...c, sortDirection: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3"><option value="asc">Eskiden yeniye</option><option value="desc">Yeniden eskiye</option></select></label>
            <div className="flex items-end gap-2"><button type="submit" className="h-11 flex-1 rounded-xl bg-fuchsia-600 px-4 font-black text-white hover:bg-fuchsia-700">Filtrele</button><button type="button" onClick={clearFilters} className="h-11 rounded-xl border border-slate-300 bg-white px-4 font-bold text-slate-700">Temizle</button></div>
          </div>
        </form>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div className="font-black text-slate-900">Randevu Listesi</div>
            <div className="text-sm font-semibold text-slate-500">Tüm {summary.total || rows.length} kayıt tek sayfada</div>
          </div>

          {loading ? (
            <div className="flex min-h-72 items-center justify-center"><div className="text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-fuchsia-600" /><div className="mt-4 font-bold text-slate-600">Randevular hazırlanıyor...</div></div></div>
          ) : rows.length === 0 ? (
            <div className="flex min-h-72 items-center justify-center px-6 text-center"><div><div className="text-5xl">📅</div><div className="mt-4 text-xl font-black">Bu filtrelerde randevu bulunamadı</div><div className="mt-2 text-slate-500">Tarih veya personel filtresini değiştirerek tekrar deneyin.</div></div></div>
          ) : (
            <>
              <div className="hidden overflow-x-auto xl:block">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Durum</th><th className="px-4 py-3">Randevu</th><th className="px-4 py-3">Alındığı Zaman</th><th className="px-4 py-3">Şube</th><th className="px-4 py-3">Personel</th><th className="px-4 py-3">Müşteri</th><th className="px-4 py-3">Hizmet / Opsiyon</th><th className="px-4 py-3">Süre</th><th className="px-4 py-3 text-right">Sipariş Tutarı</th><th className="px-4 py-3">Sipariş</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((row) => (
                      <tr key={row.id || row.reservationId} className="align-top hover:bg-slate-50">
                        <td className="px-4 py-4"><div className="space-y-2"><StatusBadge value={row.status} /><TimingBadge row={row} /></div></td>
                        <td className="px-4 py-4"><AppointmentDate row={row} /></td>
                        <td className="px-4 py-4 font-semibold text-slate-700">{formatDateTime(row.bookedAt)}</td>
                        <td className="px-4 py-4"><div className="font-black">{row.branchName || "-"}</div><div className="mt-1 text-xs text-slate-500">{row.branchCity || `Şube ID: ${row.subeId || "-"}`}</div></td>
                        <td className="px-4 py-4"><div className="font-black">{row.personnelName || (row.mode === "day" ? "Personelsiz" : "-")}</div><div className="mt-1 text-xs text-slate-500">{row.personelId || ""}</div></td>
                        <td className="px-4 py-4 font-bold">{row.customerName || "İsimsiz müşteri"}</td>
                        <td className="max-w-[320px] px-4 py-4"><div className="font-black text-slate-900">{row.serviceName || "Hizmet"}</div>{row.optionNames?.length ? <div className="mt-2 flex flex-wrap gap-1">{row.optionNames.map((name, index) => <span key={`${name}-${index}`} className="rounded-lg bg-fuchsia-50 px-2 py-1 text-xs font-bold text-fuchsia-700">{name}</span>)}</div> : null}</td>
                        <td className="px-4 py-4 font-bold">{formatDuration(row.durationMinutes, row.durationDays)}</td>
                        <td className="px-4 py-4 text-right font-black">{formatTRY(row.totalPrice)}</td>
                        <td className="px-4 py-4"><div className="font-mono text-xs font-bold">{row.orderNumber || "-"}</div><div className="mt-1 text-xs text-slate-500">{paymentLabel(row.paymentMethod)} · {row.kiosk || "-"}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-4 p-4 xl:hidden">
                {rows.map((row) => (
                  <article key={row.id || row.reservationId} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3"><div className="space-y-2"><StatusBadge value={row.status} /> <TimingBadge row={row} /></div><div className="text-right text-lg font-black">{formatTRY(row.totalPrice)}</div></div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div><div className="text-xs font-black uppercase text-slate-400">Randevu</div><div className="mt-1"><AppointmentDate row={row} /></div></div>
                      <div><div className="text-xs font-black uppercase text-slate-400">Alındığı Zaman</div><div className="mt-1 font-bold">{formatDateTime(row.bookedAt)}</div></div>
                      <div><div className="text-xs font-black uppercase text-slate-400">Şube / Personel</div><div className="mt-1 font-black">{row.branchName || "-"}</div><div className="text-sm text-slate-600">{row.personnelName || (row.mode === "day" ? "Personelsiz" : "-")}</div></div>
                      <div><div className="text-xs font-black uppercase text-slate-400">Müşteri</div><div className="mt-1 font-black">{row.customerName || "İsimsiz müşteri"}</div></div>
                    </div>
                    <div className="mt-4 rounded-xl bg-slate-50 p-3"><div className="font-black">{row.serviceName || "Hizmet"}</div>{row.optionNames?.length ? <div className="mt-2 flex flex-wrap gap-1">{row.optionNames.map((name, index) => <span key={`${name}-${index}`} className="rounded-lg bg-white px-2 py-1 text-xs font-bold text-fuchsia-700">{name}</span>)}</div> : null}<div className="mt-2 text-sm font-semibold text-slate-600">Süre: {formatDuration(row.durationMinutes, row.durationDays)}</div></div>
                    <div className="mt-3 text-xs text-slate-500">Sipariş: <span className="font-mono font-bold">{row.orderNumber || "-"}</span> · {paymentLabel(row.paymentMethod)} · {row.kiosk || "-"}</div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>

      </main>
    </div>
  );
}

export default AppointmentsPage;
